if (!customElements.get('nosht-fuel-finder')) {
  customElements.define(
    'nosht-fuel-finder',
    class NoshtFuelFinder extends HTMLElement {
      connectedCallback() {
        queueMicrotask(() => this.init());
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.initialized = false;
      }

      init() {
        if (!this.isConnected || this.initialized) return;

        this.form = this.querySelector('[data-fuel-form]');
        this.status = this.querySelector('[data-fuel-status]');
        this.resetButton = this.querySelector('[data-fuel-reset]');
        this.fields = Array.from(this.querySelectorAll('[data-fuel-field]'));
        this.productGrid = this.querySelector('[data-fuel-product-grid]');
        const products = Array.from(this.querySelectorAll('[data-fuel-product]'));

        // Keep the editor order across reconnects after the cards have been ranked.
        if (!this.products || products.length !== this.products.length ||
          products.some((product) => !this.products.includes(product))) {
          this.products = products;
        }

        if (!this.form || !this.status || !this.productGrid || this.fields.length !== 3) return;

        this.controller = new AbortController();
        const listenerOptions = { signal: this.controller.signal };

        this.form.addEventListener('change', this.onChange, listenerOptions);
        this.resetButton?.addEventListener('click', this.onReset, listenerOptions);

        this.setAttribute('data-ready', '');
        this.update();
        this.initialized = true;
      }

      onChange = () => {
        this.update();
      };

      onReset = () => {
        this.form.reset();
        this.update();

        const firstOption = this.form.querySelector('[data-fuel-option="activity"]');
        firstOption?.focus({ preventScroll: true });
      };

      update() {
        const selections = {
          activity: this.getSelection('activity'),
          timing: this.getSelection('timing'),
          goal: this.getSelection('goal')
        };

        let currentStep = null;
        this.fields.forEach((field) => {
          const key = field.dataset.fuelField;
          const isLocked = currentStep !== null;

          // Restored answers must form a continuous sequence from the first step.
          if (isLocked) {
            selections[key] = '';
            field.querySelectorAll('[data-fuel-option]:checked').forEach((option) => {
              option.checked = false;
            });
          }

          const isAnswered = Boolean(selections[key]);
          const isCurrent = !isLocked && !isAnswered;
          if (isCurrent) currentStep = field;

          field.disabled = isLocked;
          field.toggleAttribute('data-answered', isAnswered);
          field.toggleAttribute('data-current', isCurrent);
          if (isCurrent) {
            field.setAttribute('aria-current', 'step');
          } else {
            field.removeAttribute('aria-current');
          }
        });

        const isComplete = Object.values(selections).every(Boolean);
        this.toggleAttribute('data-complete', isComplete);

        if (!isComplete) {
          this.restoreProductOrder();
          this.products.forEach((product) => {
            product.hidden = false;
          });
          if (this.resetButton) this.resetButton.hidden = true;
          this.status.textContent = this.dataset.incompleteText || '';
          return;
        }

        const resultLimit = this.clampNumber(this.dataset.resultLimit, 2, 4, 4);
        const minimumResults = this.clampNumber(this.dataset.minResults, 1, resultLimit, 2);
        const rankedProducts = this.rankProducts(selections);
        const exactMatches = rankedProducts.filter((entry) => entry.exact);
        const selectedProducts = exactMatches.slice(0, resultLimit);

        if (selectedProducts.length < minimumResults) {
          const fallbackProducts = rankedProducts.filter(
            (entry) => entry.goalMatch && !selectedProducts.includes(entry)
          );
          for (const fallbackProduct of fallbackProducts) {
            selectedProducts.push(fallbackProduct);
            if (selectedProducts.length >= minimumResults || selectedProducts.length >= resultLimit) break;
          }
        }

        const selectedElements = new Set(selectedProducts.map((entry) => entry.element));
        this.products.forEach((product) => {
          product.hidden = !selectedElements.has(product);
        });
        this.orderProducts(selectedProducts);

        const resultCount = selectedElements.size;
        if (this.resetButton) this.resetButton.hidden = false;

        if (resultCount === 0) {
          this.status.textContent = this.dataset.emptyText || '';
          return;
        }

        const resultTemplate = this.dataset.resultText || 'Showing [count] curated products.';
        this.status.textContent = resultTemplate.replace(/\[count\]/g, String(resultCount));
      }

      orderProducts(selectedProducts) {
        const selectedElements = selectedProducts.map((entry) => entry.element);
        this.setProductOrder([
          ...selectedElements,
          ...this.products.filter((product) => !selectedElements.includes(product))
        ]);
      }

      restoreProductOrder() {
        this.setProductOrder(this.products);
      }

      setProductOrder(products) {
        const activeElement = document.activeElement;
        const focusedProduct = this.products.find((product) => product.contains(activeElement));

        products.forEach((product, index) => {
          product.style.removeProperty('order');
          const reference = this.productGrid.children[index] || null;
          if (product === reference) return;

          // Move the existing cards so reading and keyboard order follow the results.
          if (typeof this.productGrid.moveBefore === 'function') {
            this.productGrid.moveBefore(product, reference);
          } else {
            this.productGrid.insertBefore(product, reference);
          }
        });

        // Older browsers may drop focus when insertBefore moves a focused card.
        if (focusedProduct && !focusedProduct.hidden && document.activeElement !== activeElement) {
          activeElement.focus({ preventScroll: true });
        }
      }

      getSelection(key) {
        return this.form.querySelector(`[data-fuel-option="${key}"]:checked`)?.value || '';
      }

      rankProducts(selections) {
        return this.products
          .map((element, index) => {
            const activityMatch = this.matches(element.dataset.activities, selections.activity);
            const timingMatch = this.matches(element.dataset.timings, selections.timing);
            const goalMatch = this.matches(element.dataset.goals, selections.goal);

            return {
              element,
              index,
              exact: activityMatch && timingMatch && goalMatch,
              goalMatch,
              score: (goalMatch ? 100 : 0) + (timingMatch ? 10 : 0) + (activityMatch ? 5 : 0)
            };
          })
          .sort((first, second) => {
            if (first.exact !== second.exact) return first.exact ? -1 : 1;
            if (first.goalMatch !== second.goalMatch) return first.goalMatch ? -1 : 1;
            if (first.score !== second.score) return second.score - first.score;
            return first.index - second.index;
          });
      }

      matches(rawValues, selectedValue) {
        const values = String(rawValues || 'all')
          .trim()
          .split(/\s+/)
          .filter(Boolean);

        return values.includes('all') || values.includes(selectedValue);
      }

      clampNumber(rawValue, minimum, maximum, fallback) {
        const parsedValue = Number.parseInt(rawValue, 10);
        if (!Number.isFinite(parsedValue)) return fallback;
        return Math.min(Math.max(parsedValue, minimum), maximum);
      }
    }
  );
}
